/**
 * Gateway-backed client adapter for search-experience config.
 *
 * Implements the same contract expected by editors:
 *   - loadSearchExperienceConfig(shopId, mode) → config object
 *   - saveSearchExperienceConfig(shopId, config, mode) → saved config / error envelope
 *   - describeSearchExperienceConfig(shopId) → schema descriptor object
 *   - getSearchComponents(shopId) → { success: true, components } from gateway describe
 *
 * Talks to kea-pro-gateway routes:
 *   GET  /:token/settings/search-experience?mode=preview
 *   PUT  /:token/settings/search-experience?mode=preview   body: { config }
 *   GET  /:token/settings/search-experience/describe
 *   POST /:token/settings/search-experience/agent-ui/preview
 *   POST /:token/settings/search-experience/agent-ui/verify
 *
 * Gateway responses:
 *   GET  → { config: <object> }            → adapter unwraps and returns the config object
 *   PUT  → { success, actions, errors }    → adapter returns the input config on success,
 *                                            or { errors } on validation failure
 *   GET describe → schema descriptor       → adapter returns body as-is
 *
 * getSearchComponents is backed by the gateway describe endpoint. Component
 * descriptors must stay in the same source of truth as gateway defaults;
 * otherwise new gateway blocks can render as unknown components in admin.
 *
 * Auth:
 *   Gateway settings routes require `Authorization: Bearer <jwt>`.
 *   The JWT is obtained via POST /login/challenge (HMAC challenge flow).
 */

export function createGatewaySearchClient({ baseUrl, token, authToken, headers = {} }) {
  if (!baseUrl) throw new Error('baseUrl is required');
  if (!token) throw new Error('token is required');
  if (!authToken) throw new Error('authToken is required');

  const defaultHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
    ...headers,
  };

  const buildUrl = (mode, options = {}) => {
    const modeParam = mode || 'preview';
    // Remove trailing slash from baseUrl to avoid double-slash
    const base = baseUrl.replace(/\/+$/, '');
    const params = new URLSearchParams({ mode: modeParam });
    if (options.editor) params.set('editor', 'true');
    if (options.experience) {
      params.set('experience', options.experience);
    }
    return `${base}/v1/installations/${encodeURIComponent(token)}/settings/search-experience?${params.toString()}`;
  };

  const buildDescribeUrl = () => {
    const base = baseUrl.replace(/\/+$/, '');
    return `${base}/v1/installations/${encodeURIComponent(token)}/settings/search-experience/describe`;
  };

  const buildBundleUrl = (path) => {
    const base = baseUrl.replace(/\/+$/, '');
    return `${base}/v1/installations/${encodeURIComponent(token)}/bundles/${path}`;
  };

  const buildAgentUiUrl = (path) => {
    const base = baseUrl.replace(/\/+$/, '');
    return `${base}/v1/installations/${encodeURIComponent(token)}/settings/search-experience/agent-ui/${path}`;
  };

  const buildStorefrontContentUrl = (path = '') => {
    const base = baseUrl.replace(/\/+$/, '');
    const suffix = path ? `/${path}` : '';
    return `${base}/v1/installations/${encodeURIComponent(token)}/storefront-content${suffix}`;
  };

  const compactBody = (body) => Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined),
  );

  /**
   * Load search experience config from gateway.
   *
   * Gateway returns { config: <object> }; this method unwraps and returns
   * the config object directly — matching the contract expected by
   * useIntegrationConfig and SearchExperienceService.
   *
   * @param {string} _shopId - Shop identifier (mapped to token at construction time)
   * @param {string} [mode='preview'] - 'preview' | 'production'
   * @returns {Promise<Object>} config object (unwrapped from gateway envelope)
   */
  async function loadSearchExperienceConfig(_shopId, mode = 'preview') {
    const url = buildUrl(mode, { editor: 'true' });
    const response = await fetch(url, {
      method: 'GET',
      headers: defaultHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
    }

    const body = await response.json();
    return body.config;
  }

  async function loadSearchExperienceDefaults(_shopId, experience, mode = 'preview') {
    if (!experience) {
      throw new Error('experience is required');
    }

    const url = buildUrl(mode, { experience });
    const response = await fetch(url, {
      method: 'GET',
      headers: defaultHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to load config defaults: ${response.status} ${response.statusText}`);
    }

    const body = await response.json();
    return body.config;
  }

  /**
   * Save search experience config to gateway.
   *
   * Gateway returns { success, actions, errors }.
   * - On success (no errors): returns the input config, matching the contract
   *   expected by SearchExperienceService.saveConfig which dispatches the
   *   return value as the post-save config.
   * - On validation failure: returns { errors } so that
   *   SearchExperienceService.saveConfig's `result.errors.length > 0` check works.
   *
   * @param {string} _shopId - Shop identifier (mapped to token at construction time)
   * @param {Object} config - Config payload to save
   * @param {string} [mode='preview'] - 'preview' | 'production'
   * @returns {Promise<Object>} config on success, { errors } on validation failure
   */
  async function saveSearchExperienceConfig(_shopId, config, mode = 'preview') {
    // An explicit copy to another lane compares against that lane's revision,
    // while retaining the source revision for the server's atomic guard.
    let outgoing = config;
    const source = config?._editorState;
    if (source?.base?.mode && source.base.mode !== mode) {
      const target = await loadSearchExperienceConfig(_shopId, mode);
      outgoing = { ...config, mode, _editorState: {
        ...target._editorState,
        source: { mode: source.base.mode, revision: source.revision },
      } };
    }
    const url = buildUrl(mode);
    const response = await fetch(url, {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ config: outgoing }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save config: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    // Preserve the full transport outcome. Runtime/service layers need warnings
    // and side-effect actions, and `success:false` is meaningful even when an
    // older gateway omits a validation errors array.
    return {
      success: result.success !== false,
      config: result.success === false ? null : (result.config || config),
      currentConfig: result.currentConfig,
      code: result.code,
      errors: result.errors || [],
      warnings: result.warnings || [],
      actions: result.actions || [],
    };
  }

  /**
   * Describe the search experience config schema.
   *
   * Calls GET /:token/settings/search-experience/describe and returns
   * the response body as-is. Used by SearchExperienceService.loadSchema()
   * to populate the Redux schema state for the v2 editor.
   *
   * @returns {Promise<Object>} Schema descriptor object from gateway
   */
  async function describeSearchExperienceConfig() {
    const url = buildDescribeUrl();
    const response = await fetch(url, {
      method: 'GET',
      headers: defaultHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to describe config: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Return component descriptors from the gateway schema describe endpoint.
   *
   * @returns {Promise<{ success: boolean, components: Object }>}
   */
  async function getSearchComponents() {
    const description = await describeSearchExperienceConfig();
    return {
      success: true,
      components: description.components || {},
    };
  }

  async function postAgentUi(path, body) {
    const response = await fetch(buildAgentUiUrl(path), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let detail = '';
      let code = '';
      try {
        const error = await response.json();
        const message = error?.error?.message
          || (typeof error?.error === 'string' ? error.error : '')
          || error?.message
          || '';
        const safeMessage = String(message).replace(/\s+/g, ' ').trim().slice(0, 240);
        detail = safeMessage ? `: ${safeMessage}` : '';
        const rawCode = String(error?.error?.code || error?.code || '').toUpperCase();
        code = /^[A-Z][A-Z0-9_]{2,63}$/.test(rawCode) ? rawCode : '';
      } catch (_error) {
        detail = '';
      }
      const failure = new Error(`Failed to ${path} agent UI configuration: ${response.status}${detail}`);
      if (code) failure.code = code;
      throw failure;
    }
    return response.json();
  }

  async function applyAgentUiPreview(_shopId, candidate) {
    return postAgentUi('preview', { candidate });
  }

  async function verifyAgentUiPreview(_shopId, expectedPreviewConfigHash) {
    return postAgentUi('verify', { expected_preview_config_hash: expectedPreviewConfigHash });
  }

  /**
   * Build the preview bundle from the current preview config.
   *
   * Uses POST /bundles/build with target='preview'. The plugin bridge must not
   * proxy this gateway call; iframe/admin owns gateway transport.
   *
   * @param {string} _shopId - Shop identifier (mapped to token at construction time)
   * @returns {Promise<{ status: string, jobId: string, jobStatus: string, meta: Object }>}
   */
  async function buildPreviewBundle(_shopId) {
    const response = await fetch(buildBundleUrl('build'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({ target: 'preview', mode: 'sync' }),
    });

    if (!response.ok) {
      throw new Error(`Failed to build preview bundle: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Promote a release candidate bundle.
   *
   * Uses POST /bundles/promote. This is exposed for callers that build and
   * promote in separate steps; most publish flows use buildAndPromoteBundle().
   *
   * @param {string} _shopId - Shop identifier (mapped to token at construction time)
   * @param {{ target?: string, stage?: string, releaseId?: string, mode?: string }} [options]
   * @returns {Promise<{ status: string, jobId: string, jobStatus: string, meta: Object }>}
   */
  async function promoteBundle(_shopId, options = {}) {
    const response = await fetch(buildBundleUrl('promote'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(compactBody({
        target: options.target,
        stage: options.stage,
        releaseId: options.releaseId,
        mode: options.mode || 'sync',
      })),
    });

    if (!response.ok) {
      throw new Error(`Failed to promote bundle: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Build a release candidate from the current production config and promote
   * it to the live bundle in a single operation.
   *
   * Uses the gateway convenience endpoint POST /bundles/build-and-promote
   * which is equivalent to build(target=bundle) + promote() in sequence.
   *
   * This is the approved publish lifecycle for config changes:
   *   1. Save config to preview mode  (validates template at gateway boundary)
   *   2. Save config to production mode
   *   3. Call buildAndPromoteBundle   (compiles and promotes the bundle)
   *
   * @param {string} _shopId - Shop identifier (mapped to token at construction time)
   * @returns {Promise<{ status: string, jobId: string, jobStatus: string, meta: Object }>}
   */
  async function buildAndPromoteBundle(_shopId) {
    const response = await fetch(buildBundleUrl('build-and-promote'), {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`Failed to build and promote bundle: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Poll a bundle job until it reaches a terminal state ('success' or 'failed').
   *
   * The build-and-promote endpoint is fire-and-forget — it returns 202 Accepted
   * immediately and the actual build/promote runs asynchronously. This method
   * polls GET /bundles/jobs/:jobId on a fixed interval until the job finishes,
   * then either resolves (on success) or rejects (on failure or timeout).
   *
   * @param {string} jobId - Job ID returned by buildAndPromoteBundle
   * @param {{ intervalMs?: number, timeoutMs?: number }} [options]
   * @returns {Promise<Object>} Resolved job document when status === 'success'
   * @throws {Error} When status === 'failed' or the polling deadline is exceeded
   */
  async function pollJobStatus(jobId, { intervalMs = 2000, timeoutMs = 30000 } = {}) {
    if (!jobId) {
      throw new Error('jobId is required');
    }

    const url = buildBundleUrl(`jobs/${encodeURIComponent(jobId)}`);
    const deadline = Date.now() + timeoutMs;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Wait before each poll so we don't hammer the gateway on the first tick
      // and also handle the common case where the job completes quickly.
      await new Promise((resolve) => setTimeout(resolve, intervalMs));

      if (Date.now() >= deadline) {
        throw new Error('Bundle build/promote timed out. Check the build status and try again.');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: defaultHeaders,
      });

      if (!response.ok) {
        throw new Error(`Failed to poll job status: ${response.status} ${response.statusText}`);
      }

      const job = await response.json();

      if (job.status === 'success') return job;
      if (job.status === 'failed') {
        throw new Error(
          job.error || `Bundle build/promote failed (job ${jobId}).`,
        );
      }
      // status === 'queued' | 'running' → keep polling
    }
  }

  /**
   * Download inline artifact manifest for a completed bundle job.
   *
   * The browser/admin app owns this gateway call, then publishes the returned
   * manifest to the local plugin bridge via artifacts.publish.
   *
   * @param {string} jobId - Completed gateway bundle job ID
   * @returns {Promise<{ jobId: string, slots: string[], manifest: Object }>}
   */
  async function downloadJobArtifacts(jobId) {
    if (!jobId) {
      throw new Error('jobId is required');
    }

    const response = await fetch(buildBundleUrl(`jobs/${encodeURIComponent(jobId)}/artifacts`), {
      method: 'GET',
      headers: defaultHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to download job artifacts: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async function loadStorefrontContent() {
    const response = await fetch(buildStorefrontContentUrl(), {
      method: 'GET',
      headers: defaultHeaders,
    });
    if (!response.ok) {
      throw new Error(`Failed to load storefront content: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async function saveStorefrontLanguages(enabledLanguages) {
    const response = await fetch(buildStorefrontContentUrl('localization/languages'), {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ enabledLanguages }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save storefront languages: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async function saveStorefrontTranslationOverrides(language, overrides) {
    if (!language) throw new Error('language is required');
    const response = await fetch(buildStorefrontContentUrl(
      `localization/${encodeURIComponent(language)}/overrides`,
    ), {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ overrides }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save storefront translations: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async function saveStorefrontTemplateOverride(templateId, source) {
    if (!templateId) throw new Error('templateId is required');
    const response = await fetch(buildStorefrontContentUrl(
      `templates/${encodeURIComponent(templateId)}/override`,
    ), {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ source }),
    });
    if (!response.ok) {
      throw new Error(`Failed to save storefront template: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  return {
    loadSearchExperienceConfig,
    loadSearchExperienceDefaults,
    saveSearchExperienceConfig,
    describeSearchExperienceConfig,
    getSearchComponents,
    applyAgentUiPreview,
    verifyAgentUiPreview,
    buildPreviewBundle,
    promoteBundle,
    buildAndPromoteBundle,
    pollJobStatus,
    downloadJobArtifacts,
    loadStorefrontContent,
    saveStorefrontLanguages,
    saveStorefrontTranslationOverrides,
    saveStorefrontTemplateOverride,
  };
}
